/** Canonical game outcome shared by runtime, wire protocol and persistence.
 * Legacy winnerSlot/EndReason remain unchanged. No transport/provider dependency. */
import { PlatformError } from './errors.js';
export const CANONICAL_COMPLETION_REASONS = ['score', 'forfeit', 'timeout', 'concede', 'defeat', 'objective'] as const;
export type CanonicalCompletionReason = (typeof CANONICAL_COMPLETION_REASONS)[number];

export interface CanonicalMatchTerminal {
  configDigest: string;
  terminalTick: number;
  teamBySlot: number[];
  outcome: CanonicalMatchOutcome;
}

export function assertValidCanonicalMatchTerminal(value: unknown): asserts value is CanonicalMatchTerminal {
  const terminal = object(value, 'canonical terminal');
  keys(terminal, ['configDigest', 'terminalTick', 'teamBySlot', 'outcome'], 'canonical terminal');
  if (typeof terminal.configDigest !== 'string' || !/^[a-f0-9]{64}$/.test(terminal.configDigest)) fail('canonical terminal', 'expected config digest');
  int(terminal.terminalTick, 'canonical terminal tick', 0, Number.MAX_SAFE_INTEGER);
  assertValidCanonicalMatchOutcome(terminal.outcome, terminal.teamBySlot as number[]);
}

export type CanonicalMatchOutcome =
  | { kind: 'completed'; reason: CanonicalCompletionReason }
  | { kind: 'slot-victory'; reason: CanonicalCompletionReason; winnerSlot: number }
  | { kind: 'team-victory'; reason: CanonicalCompletionReason; winnerTeam: number }
  /** Dense ranks in canonical slot order: [1, 1, 2] permits tied placements. */
  | { kind: 'placement'; reason: CanonicalCompletionReason; rankBySlot: number[] }
  | { kind: 'draw'; reason: 'score' | 'timeout' | 'agreement' }
  | { kind: 'no-contest'; reason: 'server-error' | 'aborted' | 'insufficient-players' };

function fail(path: string, detail: string): never { throw new PlatformError('validation', `invalid ${path}: ${detail}`); }
function object(value: unknown, path: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) fail(path, 'expected object');
  return value as Record<string, unknown>;
}
function keys(value: Record<string, unknown>, allowed: readonly string[], path: string): void {
  for (const key of allowed) if (!Object.hasOwn(value, key)) fail(path, `missing ${key}`);
  for (const key of Object.keys(value)) if (!allowed.includes(key)) fail(path, `unknown field ${key}`);
}
function int(value: unknown, path: string, min: number, max: number): number {
  if (!Number.isSafeInteger(value) || (value as number) < min || (value as number) > max) fail(path, `expected integer in [${min}, ${max}]`);
  return value as number;
}
function validateResultTeams(value: readonly number[]): void {
  if (!Array.isArray(value) || value.length < 1 || value.length > 16) fail('MatchResultV2.teamBySlot', 'expected bounded non-empty canonical teams');
  const seen = new Set<number>();
  value.forEach((raw, slot) => {
    const team = int(raw, `MatchResultV2.teamBySlot[${slot}]`, 0, value.length - 1);
    if (!seen.has(team) && team !== seen.size) fail('MatchResultV2.teamBySlot', 'team ids must be contiguous in first-seat order');
    seen.add(team);
  });
}
export function assertValidCanonicalMatchOutcome(value: unknown, teamBySlot: readonly number[]): asserts value is CanonicalMatchOutcome {
  validateResultTeams(teamBySlot);
  const outcome = object(value, 'MatchOutcomeV2');
  const teamCount = new Set(teamBySlot).size;
  switch (outcome.kind) {
    case 'completed':
      keys(outcome, ['kind', 'reason'], 'MatchOutcomeV2');
      break;
    case 'slot-victory':
      keys(outcome, ['kind', 'reason', 'winnerSlot'], 'MatchOutcomeV2');
      int(outcome.winnerSlot, 'MatchOutcomeV2.winnerSlot', 0, teamBySlot.length - 1);
      break;
    case 'team-victory':
      keys(outcome, ['kind', 'reason', 'winnerTeam'], 'MatchOutcomeV2');
      int(outcome.winnerTeam, 'MatchOutcomeV2.winnerTeam', 0, teamCount - 1);
      if (teamCount < 2) fail('MatchOutcomeV2', 'team victory requires opposing teams');
      break;
    case 'placement': {
      keys(outcome, ['kind', 'reason', 'rankBySlot'], 'MatchOutcomeV2');
      if (!Array.isArray(outcome.rankBySlot) || outcome.rankBySlot.length !== teamBySlot.length) fail('MatchOutcomeV2.rankBySlot', 'must align with every canonical slot');
      const ranks = outcome.rankBySlot.map((rank, slot) => int(rank, `MatchOutcomeV2.rankBySlot[${slot}]`, 1, teamBySlot.length));
      const unique = [...new Set(ranks)].sort((a, b) => a - b);
      if (unique.some((rank, index) => rank !== index + 1)) fail('MatchOutcomeV2.rankBySlot', 'must use dense ranks starting at one');
      break;
    }
    case 'draw':
      keys(outcome, ['kind', 'reason'], 'MatchOutcomeV2');
      if (!['score', 'timeout', 'agreement'].includes(outcome.reason as string)) fail('MatchOutcomeV2.reason', 'unsupported draw reason');
      return;
    case 'no-contest':
      keys(outcome, ['kind', 'reason'], 'MatchOutcomeV2');
      if (!['server-error', 'aborted', 'insufficient-players'].includes(outcome.reason as string)) fail('MatchOutcomeV2.reason', 'unsupported no-contest reason');
      return;
    default:
      fail('MatchOutcomeV2.kind', 'unsupported terminal outcome');
  }
  if (!CANONICAL_COMPLETION_REASONS.includes(outcome.reason as CanonicalCompletionReason)) fail('MatchOutcomeV2.reason', 'unsupported completion reason');
}
